import { useEffect, useState, Fragment } from 'react';
import { admin_api } from '@/lib/api';
import { toast } from 'sonner';
import { CaretDown, CaretRight, UserCircle, Van } from '@phosphor-icons/react';

function AssignPanel({ booking, onDone }) {
  const [avail, setAvail] = useState(null);
  const [driverId, setDriverId] = useState(booking.assigned_driver_id || '');
  const [vehicleId, setVehicleId] = useState(booking.assigned_vehicle_id || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    admin_api.availability({ date: booking.pickup_date, time: booking.pickup_time, booking_id: booking.id })
      .then(setAvail)
      .catch(() => setAvail({ drivers: [], vehicles: [] }));
  }, [booking.id, booking.pickup_date, booking.pickup_time]);

  const save = async (force = false) => {
    setBusy(true);
    try {
      await admin_api.assignBooking(booking.id, { driver_id: driverId || null, vehicle_id: vehicleId || null, force });
      toast.success('Assignment saved.');
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.conflicts) {
        const list = detail.conflicts.map(c => `${c.full_name} @ ${c.pickup_time}`).join(', ');
        if (window.confirm(`Conflict: already booked near this time (${list}). Assign anyway?`)) {
          await save(true);
          return;
        }
      } else {
        toast.error('Could not assign.');
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 p-4 rounded-xl bg-white border border-neutral-200" data-testid={`assign-panel-${booking.id}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-ink-muted mb-3">Dispatch assignment</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label-base flex items-center gap-1"><UserCircle size={14} /> Driver</label>
          <select value={driverId} onChange={e => setDriverId(e.target.value)} data-testid={`assign-driver-${booking.id}`} className="input-base !py-2 text-sm">
            <option value="">— Unassigned —</option>
            {(avail?.drivers || []).map(d => (
              <option key={d.id} value={d.id}>{d.full_name}{d.available ? '' : ' (busy)'}{d.status === 'on_shift' ? ' · on shift' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-base flex items-center gap-1"><Van size={14} /> Vehicle</label>
          <select value={vehicleId} onChange={e => setVehicleId(e.target.value)} data-testid={`assign-vehicle-${booking.id}`} className="input-base !py-2 text-sm">
            <option value="">— Unassigned —</option>
            {(avail?.vehicles || []).map(v => (
              <option key={v.id} value={v.id}>{v.name}{v.available ? '' : ' (busy)'}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={() => save(false)} disabled={busy || !avail} data-testid={`assign-save-${booking.id}`} className="btn-primary !py-2 !px-4 text-xs w-full md:w-auto">
            {busy ? 'Saving…' : 'Save assignment'}
          </button>
        </div>
      </div>
      {!avail && <div className="text-xs text-ink-muted mt-2">Checking availability…</div>}
    </div>
  );
}

const CONFIG = {
  enquiries: {
    title: 'Enquiries', fetch: admin_api.enquiries, update: admin_api.updateEnquiry,
    statuses: ['new', 'in_progress', 'resolved', 'archived'],
    columns: [
      { k: 'full_name', l: 'Name' },
      { k: 'email', l: 'Email' },
      { k: 'phone', l: 'Phone' },
      { k: 'service_interest', l: 'Service' },
    ],
    detail: (e) => [
      ['Subject', e.subject], ['Message', e.message],
      ['Source', e.source], ['Notes', e.notes],
    ],
  },
  bookings: {
    title: 'Bookings', fetch: admin_api.bookings, update: admin_api.updateBooking,
    statuses: ['pending', 'confirmed', 'assigned', 'en_route', 'arrived', 'in_progress', 'completed', 'cancelled', 'no_show'],
    columns: [
      { k: 'full_name', l: 'Name' },
      { k: 'service_type', l: 'Service' },
      { k: 'pickup_date', l: 'Date' },
      { k: 'driver_assigned', l: 'Driver' },
      { k: 'vehicle_assigned', l: 'Vehicle' },
    ],
    detail: (e) => [
      ['Pickup', `${e.pickup_address} @ ${e.pickup_date} ${e.pickup_time}`],
      ['Drop-off', e.dropoff_address],
      ['Return', e.return_trip ? e.return_time : 'No'],
      ['Passengers', e.passengers],
      ['Wheelchair', e.wheelchair_accessible ? `Yes${e.wheelchair_type ? ' — ' + e.wheelchair_type : ''}` : 'No'],
      ['Service animal', e.service_animal ? 'Yes' : 'No'],
      ['Carer travelling', e.carer_travelling ? 'Yes' : 'No'],
      ['Support category', e.support_category],
      ['Recurring', e.recurring ? `${e.recurrence_pattern}${e.recurrence_end_date ? ' until ' + e.recurrence_end_date : ''}` : 'No'],
      ['NDIS #', e.ndis_number],
      ['Funding', e.funding_source],
      ['Coordinator', e.support_coordinator_name],
      ['Special requirements', e.special_requirements],
      ['Driver notes', e.driver_notes],
    ],
    assignable: true,
  },
  quotes: {
    title: 'Quotes', fetch: admin_api.quotes, update: admin_api.updateQuote,
    statuses: ['new', 'quoted', 'accepted', 'declined', 'expired'],
    columns: [
      { k: 'full_name', l: 'Name' },
      { k: 'service_type', l: 'Service' },
      { k: 'pickup_suburb', l: 'From' },
      { k: 'dropoff_suburb', l: 'To' },
    ],
    detail: (e) => [
      ['Frequency', e.frequency], ['Passengers', e.passengers],
      ['Distance (km)', e.estimated_distance_km], ['Wheelchair', e.wheelchair_accessible ? 'Yes' : 'No'],
      ['Funding', e.funding_source], ['Additional', e.additional_info],
    ],
  },
  referrals: {
    title: 'Referrals', fetch: admin_api.referrals, update: admin_api.updateReferral,
    statuses: ['new', 'contacted', 'onboarded', 'declined'],
    columns: [
      { k: 'referrer_name', l: 'Referrer' },
      { k: 'referrer_type', l: 'Type' },
      { k: 'participant_name', l: 'Participant' },
      { k: 'urgency', l: 'Urgency' },
    ],
    detail: (e) => [
      ['Organisation', e.referrer_organisation],
      ['Referrer email', e.referrer_email],
      ['Referrer phone', e.referrer_phone],
      ['Participant NDIS', e.participant_ndis_number],
      ['Participant contact', e.participant_contact],
      ['Services', (e.services_needed || []).join(', ')],
      ['Notes', e.notes],
    ],
  },
};

export default function AdminList({ kind }) {
  const cfg = CONFIG[kind];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await cfg.fetch({ status: statusFilter || undefined });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, kind]);

  const updateStatus = async (id, status) => {
    try {
      await cfg.update(id, { status });
      toast.success('Status updated.');
      load();
    } catch { toast.error('Could not update.'); }
  };

  return (
    <div data-testid={`admin-list-${kind}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-2">Operations</div>
          <h1 className="font-display font-extrabold text-3xl text-ink">{cfg.title}</h1>
          <p className="text-ink-muted text-sm mt-1">{total} total · showing {items.length}</p>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid={`admin-filter-${kind}`}
          className="input-base !w-auto"
        >
          <option value="">All statuses</option>
          {cfg.statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left">
              <tr>
                <th className="p-4 w-8"></th>
                {cfg.columns.map(c => <th key={c.k} className="p-4 font-semibold text-ink text-xs uppercase tracking-widest">{c.l}</th>)}
                <th className="p-4 font-semibold text-ink text-xs uppercase tracking-widest">Status</th>
                <th className="p-4 font-semibold text-ink text-xs uppercase tracking-widest">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cfg.columns.length + 3} className="p-8 text-center text-ink-muted">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={cfg.columns.length + 3} className="p-8 text-center text-ink-muted">No records yet.</td></tr>
              )}
              {items.map(item => {
                const open = openId === item.id;
                return (
                  <Fragment key={item.id}>
                    <tr className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setOpenId(open ? null : item.id)}>
                      <td className="p-4 text-ink-muted">
                        {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                      </td>
                      {cfg.columns.map(c => <td key={c.k} className="p-4 text-ink">{item[c.k] || '—'}</td>)}
                      <td className="p-4">
                        <select
                          value={item.status}
                          onClick={e => e.stopPropagation()}
                          onChange={e => updateStatus(item.id, e.target.value)}
                          data-testid={`admin-status-${kind}-${item.id}`}
                          className="rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-brand/30 outline-none bg-white"
                        >
                          {cfg.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-4 text-ink-muted text-xs">{item.created_at ? new Date(item.created_at).toLocaleString('en-AU') : ''}</td>
                    </tr>
                    {open && (
                      <tr className="bg-neutral-50/60">
                        <td></td>
                        <td colSpan={cfg.columns.length + 2} className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cfg.detail(item).map(([k, v]) => (
                              <div key={k} className="flex gap-3 text-sm">
                                <span className="text-ink-muted font-semibold w-32 shrink-0">{k}</span>
                                <span className="text-ink">{v || '—'}</span>
                              </div>
                            ))}
                          </div>
                          {cfg.assignable && <AssignPanel booking={item} onDone={load} />}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
